import React from "react";
import type { BillingStatusResponse } from "../lib/billing-api.js";
import { PeekingCorgi, CorgiPaws } from "./PeekingCorgi.js";

export interface SubscriptionStatusCardProps {
  status: BillingStatusResponse | null | undefined;
  isLoading?: boolean;
  showCorgi?: boolean;
  showDetails?: boolean;
}

export function SubscriptionStatusCard({
  status,
  isLoading,
  showCorgi = true,
  showDetails = false,
}: SubscriptionStatusCardProps): React.ReactElement {
  if (isLoading) {
    return (
      <div style={{ position: "relative", marginTop: "0" }}>
        {showCorgi && (
          <>
            <div
              data-testid="peeking-corgi"
              style={{
                position: "absolute",
                top: "-62px",
                right: "22px",
                zIndex: 0,
                pointerEvents: "none",
              }}
            >
              <PeekingCorgi width={65} height={83} />
            </div>
            <div
              data-testid="peeking-corgi-paws"
              style={{
                position: "absolute",
                top: "-5px",
                right: "36px",
                zIndex: 2,
                pointerEvents: "none",
              }}
            >
              <CorgiPaws width={50} height={20} />
            </div>
          </>
        )}
        <div
          data-testid="subscription-card-skeleton"
          style={{
            backgroundColor: "var(--color-paper)",
            borderRadius: "var(--radius-card)",
            boxShadow: "var(--shadow-card)",
            padding: "20px",
            display: "flex",
            flexDirection: "column",
            gap: "12px",
            position: "relative",
            zIndex: 1,
          }}
        >
          <div
            style={{
              width: "40%",
              height: "16px",
              backgroundColor: "var(--color-canvas)",
              borderRadius: "8px",
            }}
          />
          <div
            style={{
              width: "70%",
              height: "24px",
              backgroundColor: "var(--color-canvas)",
              borderRadius: "8px",
            }}
          />
        </div>
      </div>
    );
  }

  const isActive = status?.isActive ?? false;
  const subStatus = status?.subscription?.status;

  let planName = "Нет";
  let badgeText = "Без доступа";
  let badgeColor = "var(--color-slate)";
  let badgeBg = "var(--color-canvas)";

  const nextBillingDate = status?.nextBillingDate
    ? new Date(status.nextBillingDate).toLocaleDateString("ru-RU", {
        day: "numeric",
        month: "long",
      })
    : null;

  const trialEndDateStr = status?.trialStartedAt
    ? (() => {
        const d = new Date(status.trialStartedAt);
        d.setDate(d.getDate() + 5);
        return d.toLocaleDateString("ru-RU", {
          day: "numeric",
          month: "long",
        });
      })()
    : null;

  if (isActive) {
    if (subStatus === "past_due") {
      planName = "Premium";
      badgeText = "Просрочен";
      badgeColor = "var(--color-warning)";
      badgeBg = "var(--color-warning-surface)";
    } else if (subStatus === "active" || subStatus === "cancelled" || subStatus === "trialing") {
      planName = "Premium";
      badgeText = "Активен";
      badgeColor = "var(--color-success)";
      badgeBg = "var(--color-success-bg)";
    } else {
      planName = "Пробный (5 дней)";
      badgeText = "Активен";
      badgeColor = "var(--color-success)";
      badgeBg = "var(--color-success-bg)";
    }
  }

  const isCancelled =
    Boolean(status?.subscription?.cancelAtPeriodEnd) || subStatus === "cancelled";

  return (
    <div style={{ position: "relative", marginTop: "0" }}>
      {showCorgi && (
        <>
          <div
            data-testid="peeking-corgi"
            style={{
              position: "absolute",
              top: "-62px",
              right: "22px",
              zIndex: 0,
              pointerEvents: "none",
            }}
          >
            <PeekingCorgi width={65} height={83} />
          </div>
          <div
            data-testid="peeking-corgi-paws"
            style={{
              position: "absolute",
              top: "-5px",
              right: "36px",
              zIndex: 2,
              pointerEvents: "none",
            }}
          >
            <CorgiPaws width={50} height={20} />
          </div>
        </>
      )}

      <div
        data-testid="subscription-status-card"
        style={{
          backgroundColor: "var(--color-paper)",
          borderRadius: "var(--radius-card)",
          boxShadow: "var(--shadow-floating)",
          padding: "20px",
          display: "flex",
          flexDirection: "column",
          gap: "16px",
          position: "relative",
          zIndex: 1,
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontSize: "12px", color: "var(--color-slate)", fontWeight: 500 }}>
              Тарифный план
            </div>
            <div style={{ fontSize: "16px", fontWeight: 700, color: "var(--color-graphite)" }}>
              {planName}
            </div>
          </div>

          <span
            data-testid="subscription-badge"
            style={{
              fontSize: "12px",
              fontWeight: 600,
              padding: "4px 10px",
              borderRadius: "var(--radius-pill)",
              backgroundColor: badgeBg,
              color: badgeColor,
            }}
          >
            {badgeText}
          </span>
        </div>

        {showDetails && (
          <div
            style={{
              paddingTop: "12px",
              borderTop: "1px solid var(--color-frost-border)",
            }}
          >
            <div style={{ fontSize: "13px", color: "var(--color-slate)" }}>
              {isActive ? (
                planName === "Пробный (5 дней)" ? (
                  trialEndDateStr
                    ? `Бесплатный пробный период до ${trialEndDateStr}, далее 199 ₽/мес.`
                    : "Действует пробный период, далее 199 ₽/мес."
                ) : isCancelled ? (
                  "Действует до конца оплаченного периода, автопродление отключено"
                ) : subStatus === "past_due" ? (
                  "Поиск действует еще 2 дня. Оплатите подписку, чтобы продолжить получать вакансии"
                ) : nextBillingDate ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                    <span>Следующее списание:</span>
                    <span style={{ fontWeight: 600, color: "var(--color-graphite)" }}>{nextBillingDate}</span>
                  </div>
                ) : (
                  "Действует подписка"
                )
              ) : (
                "Оплатите подписку, чтобы получать вакансии"
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
